import { Injectable } from '@angular/core';
import {
  QuestionGroupSortAttribute,
  SortDirection
} from '@generated/base-types';
import { FileSaverService } from '@new/common/modal/export-question-list-modal/export-service/file-saver.service';
import { takeGraphQLResult } from '@new/common/operators/take-graphql-response';
import { parseError } from '@new/common/utils/error-parser';
import {
  assertIsDefined,
  isDefined
} from '@new/common/utils/type-guards/is-defined';
import { TranslateService } from '@ngx-translate/core';
import { Action, State, Store } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { catchError, filter, Observable, switchMap, take, tap } from 'rxjs';
import { ToastsService } from '../../../../common/services/toasts.service';
import { AssignLabelsGQL } from '../../lib/services/labels/assign-labels.generated';
import { AssignExamsGQL } from '../../lib/services/tasks/assign-exam.generated';
import { DeleteQuestionGroupsGQL } from '../../lib/services/tasks/delete-question-groups.generated';
import { LoadTaskQuestionGroupsGQL } from '../../lib/services/tasks/load-question-groups.generated';
import { RevisionDocumentGQL } from '../../lib/services/tasks/revision-document.generated';
import { SetAffiliationGQL } from '../../lib/services/tasks/set-affiliation.generated';
import { SetBlueprintGQL } from '../../lib/services/tasks/set-blueprint.generated';
import { SetRevisionStatusGQL } from '../../lib/services/tasks/set-revision-status.generated';
import { SetRevisionYearGQL } from '../../lib/services/tasks/set-revision-year.generated';
import { SetSourceLanguageGQL } from '../../lib/services/tasks/set-source-language.generated';
import { SetSupervisorGQL } from '../../lib/services/tasks/set-supervisor.generated';
import { updateLabels } from '../../lib/utils/update-labels';
import {
  selectPoolAffiliations,
  selectPoolId,
  selectPoolLabels,
  selectPoolRevisionStatuses,
  selectPoolUnlockedExams
} from '../pool/pool.state.model';
import * as Act from './task-question-groups.actions';
import {
  TaskQuestionGroupsStateModel,
  taskQuestionGroupsStateDefaults as taskQuestionGroupsStateDefaults
} from './task-question-groups.state.model';

@State<TaskQuestionGroupsStateModel>({
  name: 'taskQuestionGroups',
  defaults: taskQuestionGroupsStateDefaults
})
@Injectable()
export class TaskQuestionGroupsState {
  constructor(
    private readonly loadTaskQuestionGroupsService: LoadTaskQuestionGroupsGQL,
    private readonly deleteQuestionGroupsService: DeleteQuestionGroupsGQL,
    private readonly setSupervisorService: SetSupervisorGQL,
    private readonly setRevisionYearService: SetRevisionYearGQL,
    private readonly setAffiliationService: SetAffiliationGQL,
    private readonly setLanguageService: SetSourceLanguageGQL,
    private readonly setBlueprintService: SetBlueprintGQL,
    private readonly setRevisionStatusService: SetRevisionStatusGQL,
    private readonly revisionDocumentGQL: RevisionDocumentGQL,
    private readonly assignExamService: AssignExamsGQL,
    private readonly assignLabelsService: AssignLabelsGQL,
    private readonly fileSaver: FileSaverService,
    private readonly toasts: ToastsService,
    private readonly translate: TranslateService,
    private readonly store: Store
  ) {}

  /* Query actions */

  @Action(Act.LoadTaskQuestionGroups)
  public loadTaskQuestionGroups(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { taskIds }: Act.LoadTaskQuestionGroups
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'loading' } }
    });

    return this.store.select(selectPoolId).pipe(
      filter(isDefined),
      take(1),
      switchMap(poolId =>
        this.loadTaskQuestionGroupsService.fetch({ poolId, taskIds })
      ),
      takeGraphQLResult(),
      switchMap(res => {
        const sorted = res.pool.taskQuestionGroups.sort((a, b) =>
          a.questionGroup.id > b.questionGroup.id ? 1 : -1
        );

        return ctx.dispatch(new Act.LoadTaskQuestionGroupsSuccess(sorted));
      }),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.LoadTaskQuestionGroupsFailure(parseError(err)))
      )
    );
  }

  @Action(Act.LoadTaskQuestionGroupsSuccess)
  public loadTaskQuestionGroupsSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { taskQuestionGroups }: Act.LoadTaskQuestionGroupsSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: taskQuestionGroups,
        query: { state: 'success' }
      }
    });
  }

  @Action(Act.LoadTaskQuestionGroupsFailure)
  public loadTaskQuestionGroupsFailure(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { error }: Act.LoadTaskQuestionGroupsFailure
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'failure', error } }
    });
  }

  @Action(Act.SetSelectedIds)
  public setSelectedIds(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { taskQuestionGroups }: Act.SetSelectedIds
  ): void {
    ctx.patchState({
      selectedIds: taskQuestionGroups.map(tqg => tqg.questionGroup.id)
    });
  }

  @Action(Act.ResetSelectedIds)
  public resetSelectedIds(
    ctx: StateContext<TaskQuestionGroupsStateModel>
  ): void {
    ctx.patchState({ selectedIds: [] });
  }

  @Action(Act.ResetTableState)
  public resetTableState(
    ctx: StateContext<TaskQuestionGroupsStateModel>
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, data: [] },
      selectedIds: []
    });
  }

  @Action(Act.SetSortAttributes)
  public setSortAttributes(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { sortAttributes }: Act.SetSortAttributes
  ): void {
    ctx.patchState({ sortAttributes });
  }

  @Action(Act.ResetSortAttributes)
  public resetSortAttributes(
    ctx: StateContext<TaskQuestionGroupsStateModel>
  ): void {
    ctx.patchState({
      sortAttributes: [
        {
          attribute: QuestionGroupSortAttribute.SequentialNumber,
          direction: SortDirection.Asc
        }
      ]
    });
  }

  /* Mutation actions */

  @Action(Act.Failure)
  public failure(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { error }: Act.Failure
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'failure', error } }
    });
    this.toasts.addWarning(this.translate.instant('error_modal.title'));
  }

  @Action(Act.DeleteQuestionGroups)
  public deleteQuestionGroups(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { questionGroupIds }: Act.DeleteQuestionGroups
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to delete question groups');

    return this.deleteQuestionGroupsService
      .mutate({ poolId, questionGroupIds })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(new Act.DeleteQuestionGroupsSuccess(questionGroupIds))
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.DeleteQuestionGroupsSuccess)
  public deleteQuestionGroupsSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { questionGroupIds }: Act.DeleteQuestionGroupsSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx
          .getState()
          .items.data?.filter(
            tqg => !questionGroupIds.includes(tqg.questionGroup.id)
          ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.destroy', {
        resource: this.translate.instant('common.models.question')
      })
    );
  }

  @Action(Act.SetSupervisor)
  public setSupervisor(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { supervisor, questionGroupIds }: Act.SetSupervisor
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set supervisor');

    return this.setSupervisorService
      .mutate({ poolId, questionGroupIds, user: supervisor })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.SetSupervisorSuccess(supervisor, questionGroupIds)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetSupervisorSuccess)
  public setSupervisorSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { supervisor, questionGroupIds }: Act.SetSupervisorSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx
          .getState()
          .items.data?.map(tqg =>
            questionGroupIds.includes(tqg.questionGroup.id)
              ? { ...tqg, questionGroup: { ...tqg.questionGroup, supervisor } }
              : tqg
          ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.supervisor')
      })
    );
  }

  @Action(Act.SetRevisionYear)
  public setRevisionYear(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { revisionYear, questionGroupIds }: Act.SetRevisionYear
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set revision year');

    return this.setRevisionYearService
      .mutate({ poolId, questionGroupIds, revisionYear })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.SetRevisionYearSuccess(revisionYear, questionGroupIds)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetRevisionYearSuccess)
  public setRevisionYearSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { revisionYear, questionGroupIds }: Act.SetRevisionYearSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(tqg =>
          questionGroupIds.includes(tqg.questionGroup.id)
            ? {
                ...tqg,
                questionGroup: { ...tqg.questionGroup, revisionYear }
              }
            : tqg
        ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.revision_year')
      })
    );
  }

  @Action(Act.SetAffiliation)
  public setAffiliation(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { affiliationId, questionGroupIds }: Act.SetAffiliation
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set affiliation');

    return this.setAffiliationService
      .mutate({ poolId, questionGroupIds, affiliationId })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.SetAffiliationSuccess(affiliationId, questionGroupIds)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetAffiliationSuccess)
  public setAffiliationSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { affiliationId, questionGroupIds }: Act.SetAffiliationSuccess
  ): void {
    const affiliation = this.store
      .selectSnapshot(selectPoolAffiliations)
      .find(({ id }) => id === affiliationId);
    assertIsDefined(affiliation);

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(tqg =>
          questionGroupIds.includes(tqg.questionGroup.id)
            ? {
                ...tqg,
                questionGroup: {
                  ...tqg.questionGroup,
                  affiliation
                }
              }
            : tqg
        ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.affiliation')
      })
    );
  }

  @Action(Act.SetSourceLanguage)
  public setSourceLanguage(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { language, questionGroupIds }: Act.SetSourceLanguage
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set source language');

    return this.setLanguageService
      .mutate({ poolId, questionGroupIds, language })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.SetSourceLanguageSuccess(language, questionGroupIds)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetSourceLanguageSuccess)
  public setSourceLanguageSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { language, questionGroupIds }: Act.SetSourceLanguageSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(tqg =>
          questionGroupIds.includes(tqg.questionGroup.id)
            ? {
                ...tqg,
                questionGroup: {
                  ...tqg.questionGroup,
                  sourceLanguage: language
                }
              }
            : tqg
        ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.sourceLanguage')
      })
    );
  }

  @Action(Act.SetBlueprint)
  public setBlueprint(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { categories, questionGroupIds }: Act.SetBlueprint
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set blueprint');

    return this.setBlueprintService
      .mutate({
        poolId,
        questionGroupIds,
        category0Id: categories[0],
        category1Id: categories[1],
        category2Id: categories[2],
        category3Id: categories[3],
        category4Id: categories[4],
        category5Id: categories[5],
        category6Id: categories[6],
        category7Id: categories[7]
      })
      .pipe(
        takeGraphQLResult(),
        switchMap(() => ctx.dispatch(new Act.SetBlueprintSuccess())),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetBlueprintSuccess)
  public setBlueprintSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>
  ): void {
    const taskIds = ctx.getState().items.data?.map(qg => qg.task.id);

    if (taskIds && taskIds.length > 0) {
      ctx.dispatch(new Act.LoadTaskQuestionGroups(taskIds.map(String)));
    }

    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'success' } }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.dimension')
      })
    );
  }

  @Action(Act.SetRevisionStatus)
  public setRevisionStatus(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { revisionStatusId, questionGroupIds }: Act.SetRevisionStatus
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set revision status');

    return this.setRevisionStatusService
      .mutate({ poolId, questionGroupIds, revisionStatusId })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.SetRevisionStatusSuccess(revisionStatusId, questionGroupIds)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetRevisionStatusSuccess)
  public setRevisionStatusSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { revisionStatusId, questionGroupIds }: Act.SetRevisionStatusSuccess
  ): void {
    const revisionStatus = this.store
      .selectSnapshot(selectPoolRevisionStatuses)
      .find(({ id }) => id === revisionStatusId);

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(tqg =>
          questionGroupIds.includes(tqg.questionGroup.id)
            ? {
                ...tqg,
                questionGroup: {
                  ...tqg.questionGroup,
                  revisionStatus:
                    revisionStatus === undefined
                      ? // eslint-disable-next-line unicorn/no-null
                        null
                      : {
                          id: revisionStatus.id,
                          shortName: revisionStatus.shortName
                        }
                }
              }
            : tqg
        ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.revision_status')
      })
    );
  }

  @Action(Act.CreateRevisionDocument)
  public createRevisionDocument(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { displayOptions }: Act.CreateRevisionDocument
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to create revision document');

    return this.revisionDocumentGQL.fetch({ poolId, displayOptions }).pipe(
      takeGraphQLResult(),
      tap(res => {
        const file = res.pool.revisionDocument?.url;
        if (file) {
          this.fileSaver.saveAs(file);
        }
      }),
      switchMap(() => ctx.dispatch(new Act.CreateRevisionDocumentSuccess())),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.CreateRevisionDocumentSuccess)
  public createRevisionDocumentSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'success' } }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.create', {
        resource: this.translate.instant('common.models.revision_document')
      })
    );
  }

  @Action(Act.AssignExam)
  public assignExam(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { questionGroupIds, examIdsToAdd, examIdsToRemove }: Act.AssignExam
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to assign exams');

    return this.assignExamService
      .mutate({ poolId, questionGroupIds, examIdsToAdd, examIdsToRemove })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.AssignExamSuccess(
              questionGroupIds,
              examIdsToAdd,
              examIdsToRemove
            )
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.AssignExamSuccess)
  public assignExamSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { questionGroupIds, examIdsToAdd, examIdsToRemove }: Act.AssignExamSuccess
  ): void {
    const unlockedExams = this.store.selectSnapshot(selectPoolUnlockedExams);

    const updatedTQGs = ctx.getState().items.data?.map(tqg => {
      if (!questionGroupIds.includes(tqg.questionGroup.id)) return tqg;

      const remainingExamsInGroup = tqg.questionGroup.futureExams.filter(
        exam => !examIdsToRemove.includes(exam.id)
      );

      const examsToAddToGroup = examIdsToAdd
        .map(id => unlockedExams.find(exam => exam.id === id))
        .filter(isDefined)
        .filter(
          exam =>
            !remainingExamsInGroup.some(existing => existing.id === exam.id)
        );

      return {
        ...tqg,
        questionGroup: {
          ...tqg.questionGroup,
          futureExams: [...remainingExamsInGroup, ...examsToAddToGroup]
        }
      };
    });

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: updatedTQGs,
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.assign_exam')
      })
    );
  }

  @Action(Act.AssignQGLabels)
  public assignQGLabels(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { labelIdsToAdd, labelIdsToRemove, questionGroupIds }: Act.AssignQGLabels
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(
      poolId,
      'Pool ID is required to assign question group labels'
    );

    return this.assignLabelsService
      .mutate({ poolId, questionGroupIds, labelIdsToAdd, labelIdsToRemove })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.AssignQGLabelsSuccess(
              labelIdsToAdd,
              labelIdsToRemove,
              questionGroupIds
            )
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.AssignQGLabelsSuccess)
  public assignQGLabelsSuccess(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    {
      labelIdsToAdd,
      labelIdsToRemove,
      questionGroupIds
    }: Act.AssignQGLabelsSuccess
  ): void {
    const allLabels = this.store.selectSnapshot(selectPoolLabels);

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(tqg =>
          questionGroupIds.includes(tqg.questionGroup.id)
            ? {
                ...tqg,
                questionGroup: {
                  ...tqg.questionGroup,
                  labels: updateLabels(
                    allLabels,
                    tqg.questionGroup.labels,
                    labelIdsToAdd,
                    labelIdsToRemove
                  )
                }
              }
            : tqg
        ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.question')
      })
    );
  }

  @Action(Act.UpdateQGLabel)
  public updateQGLabel(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { label: updatedLabel }: Act.UpdateQGLabel
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(tqg => {
          const hasLabel = tqg.questionGroup.labels.some(
            l => l.id === updatedLabel.id
          );

          return hasLabel
            ? {
                ...tqg,
                questionGroup: {
                  ...tqg.questionGroup,
                  labels: tqg.questionGroup.labels.map(l =>
                    l.id === updatedLabel.id ? updatedLabel : l
                  )
                }
              }
            : tqg;
        }),
        mutation: { state: 'success' }
      }
    });
  }

  @Action(Act.RemoveQGLabel)
  public removeQGLabel(
    ctx: StateContext<TaskQuestionGroupsStateModel>,
    { id }: Act.RemoveQGLabel
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(tqg => ({
          ...tqg,
          questionGroup: {
            ...tqg.questionGroup,
            labels: tqg.questionGroup.labels.filter(l => l.id !== id)
          }
        })),
        mutation: { state: 'success' }
      }
    });
  }
}
